import * as React from 'react';
import { cn } from '@/hen-ui/utils';
import {
  articleHeroBannerVariants,
  articleHeroBannerColorConfig,
} from './article-hero-banner.variants';
import { Avatar, AvatarGroup } from '@/hen-ui/atomics/Avatar';
import { Text } from '@/hen-ui/atomics/Text';
import { Button, type ButtonProps } from '@/hen-ui/atomics/Button';
import { Breadcrumb } from '@/hen-ui/composites/Breadcrumb';
import { Flex } from '@/hen-ui/composites/Flex';
import { SectionHeader } from '@/hen-ui/composites/SectionHeader';
import { IconName } from 'lucide-react/dynamic';

export interface ArticleHeroContributor {
  name: string;
  initials: string;
  src?: string;
}

export interface ArticleHeroActionButtonConfig
  extends Omit<ButtonProps, 'children'> {
  text?: string;
}

export interface ArticleHeroBannerProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Main title (H1) */
  title: string;
  /** Optional subtext or paragraph below the title */
  subtext?: string;
  /** Custom breadcrumb (full override). When not provided, uses Breadcrumb with breadcrumbItems. */
  breadcrumb?: React.ReactNode;
  /** Breadcrumb trail when using the default Breadcrumb. Omit or pass [] to hide the section; pass at least one item to show (e.g. [{ label: 'Home' }]). Ignored if breadcrumb is provided. */
  breadcrumbItems?: { label: string; href?: string }[];
  /** Optional action button top-right (e.g. "Save this article"). Uses Button props – text, href, iconBefore, iconAfter, etc. */
  showActionButton?: boolean;
  /** Action button text (e.g. "Save this article") */
  actionButtonText?: string;
  /** Action button icon (e.g. "heart") */
  actionButtonIcon?: IconName;
  /** Written by – name and optional avatar */
  writtenBy?: ArticleHeroContributor;
  /** Edited by – name and optional avatar */
  editedBy?: ArticleHeroContributor;
  /** Reviewed by – name and optional avatar */
  reviewedBy?: ArticleHeroContributor;
  /** Optional date string – e.g. published date (e.g. "September 25, 2025") */
  date?: string;
  /** Optional "Updated on" date – when set (e.g. from WordPress modified date), only "Updated on {date}" is shown (published date is not shown) */
  updatedOn?: string;
  /** Color scheme: neutral (white bg, dark text) or secondary (purple bg, white text) */
  color?: 'neutral' | 'secondary';
}

/**
 * ArticleHeroBanner component following HEN Design System standards.
 *
 * Hero section for article pages. Supports two color schemes: neutral (white bg,
 * dark text) or secondary (purple bg, white text). Optional breadcrumb, title,
 * subtext, contributor avatars and credits, and date.
 * Uses design tokens from tailwind.config.ts.
 *
 * @example
 * // Purple hero (default)
 * <ArticleHeroBanner
 *   color="secondary"
 *   title="Section Title"
 *   subtext="Subtext or Paragraph"
 *   breadcrumbItems={[{ label: "Home", href: "/" }, { label: "Section Title" }]}
 *   writtenBy={{ name: "Dr. Tiffany Sanders", initials: "TS" }}
 *   date="September 25, 2025"
 *   actionButtonIcon="heart"
 *   actionButtonText="Save this article to your dashboard"
 * />
 *
 * @example
 * // White / neutral
 * <ArticleHeroBanner color="neutral" title="Section Title" subtext="Subtext" />
 */
export const ArticleHeroBanner = React.forwardRef<
  HTMLDivElement,
  ArticleHeroBannerProps
>(
  (
    {
      className,
      title,
      subtext,
      breadcrumb,
      breadcrumbItems,
      showActionButton = true,
      actionButtonText = 'Save this article to your dashboard',
      actionButtonIcon = 'heart',
      writtenBy,
      editedBy,
      reviewedBy,
      date,
      updatedOn,
      color = 'neutral',
      ...props
    },
    ref,
  ) => {
    const content = articleHeroBannerColorConfig[color];
    const contributors = [writtenBy, editedBy, reviewedBy].filter(Boolean) as
      | ArticleHeroContributor[]
      | undefined;
    const breadcrumbItemsResolved = breadcrumbItems ?? [];
    const hasBreadcrumbs =
      breadcrumb != null || breadcrumbItemsResolved.length > 0;

    return (
      <Flex
        ref={ref}
        direction='column'
        className={cn(articleHeroBannerVariants({ color }), className)}
        data-name='Article Hero Banner'
        {...props}
      >
        <Flex
          direction='column'
          gap='xl'
          className='w-full max-w-content mx-auto shrink-0'
        >
          <Flex
            direction='row'
            wrap='wrap'
            align='center'
            justify={hasBreadcrumbs ? 'between' : 'end'}
            className='w-full shrink-0 gap-y-1'
          >
            {hasBreadcrumbs && (
              <Flex>
                {breadcrumb ?? (
                  <Breadcrumb
                    items={breadcrumbItemsResolved}
                    color={content.breadcrumbColor}
                    className='pl-0'
                  />
                )}
              </Flex>
            )}
            {showActionButton && (
              <Button
                variant={content.buttonVariant}
                iconAfter={actionButtonIcon}
                // todo: add onClick handler
                // onClick={actionButtonOnClick}
              >
                {actionButtonText}
              </Button>
            )}
          </Flex>

          {/* Title + subtext */}
          <SectionHeader
            size='h1'
            title={title}
            subtext={subtext}
            align='left'
            color={content.sectionHeaderColor}
          />

          {/* About article: avatars + credits + date */}
          <Flex direction='column' gap='xxs'>
            {(contributors?.length ?? 0) > 0 && (
              <Flex
                direction='column'
                gap='sm'
                className='w-full md:flex-row md:items-center'
              >
                <AvatarGroup className={content.avatarGroupClassName}>
                  {contributors?.map((c, i) => (
                    <Avatar
                      key={i}
                      alt={c.name}
                      fallback={c.initials}
                      src={c.src}
                      size='lg'
                    />
                  ))}
                </AvatarGroup>
                <Flex
                  direction='column'
                  gap='xxs'
                  wrap='wrap'
                  className='min-w-0 flex-1 sm:flex-row sm:items-baseline sm:gap-sm'
                >
                  {writtenBy && (
                    <Flex asChild direction='row' gap='xxs'>
                      <Text tag='span'>
                        <Text variant='b5' tag='span' color={content.textColor}>
                          Written by
                        </Text>
                        <Text
                          variant='b5'
                          weight='bold'
                          tag='span'
                          color={content.textColor}
                        >
                          {writtenBy.name}
                        </Text>
                      </Text>
                    </Flex>
                  )}
                  {editedBy && (
                    <Flex asChild direction='row' gap='xxs'>
                      <Text tag='span'>
                        <Text variant='b5' tag='span' color={content.textColor}>
                          Edited by
                        </Text>
                        <Text
                          variant='b5'
                          weight='bold'
                          tag='span'
                          color={content.textColor}
                        >
                          {editedBy.name}
                        </Text>
                      </Text>
                    </Flex>
                  )}
                  {reviewedBy && (
                    <Flex asChild direction='row' gap='xxs'>
                      <Text tag='span'>
                        <Text variant='b5' tag='span' color={content.textColor}>
                          Reviewed by
                        </Text>
                        <Text
                          variant='b5'
                          weight='bold'
                          tag='span'
                          color={content.textColor}
                        >
                          {reviewedBy.name}
                        </Text>
                      </Text>
                    </Flex>
                  )}
                </Flex>
              </Flex>
            )}
            {(date || updatedOn) && (
              <Flex direction='row' align='center' gap='xxs'>
                <Text variant='l1' color={content.textColor}>
                  {updatedOn ? `Updated on ${updatedOn}` : date}
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
    );
  },
);

ArticleHeroBanner.displayName = 'ArticleHeroBanner';
